import { saveAs } from "file-saver";
import type { ChatTablePayload } from "@shared/api/chatApi";
import type { ChatRoomTurn } from "@shared/api/chatRoomApi";
import { formatChatDate, formatChatTime, formatTurnTime } from "@shared/api/chatRoomApi";
import { global } from "@shared/libs/Global";

/** 복사/저장 결과 — UI 안내용. */
export type ExportResult = "copied" | "downloaded";

/** 보안 컨텍스트(HTTPS/localhost)에서만 클립보드 이미지 쓰기가 가능하다. */
function canCopyImage(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext === true &&
    typeof ClipboardItem !== "undefined" &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.write === "function"
  );
}

function canCopyText(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  );
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/** 파일명으로 안전한 문자열로 정리하고 날짜를 붙인다. */
export function exportFileName(title: string | undefined, ext: string): string {
  const base = (title || "chatbot")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  const today = new Date().toISOString().slice(0, 10);
  return `${base || "chatbot"}_${today}.${ext}`;
}

/** 차트 이미지: HTTPS면 클립보드 복사, 아니면(또는 실패 시) 다운로드로 폴백. */
export async function copyOrDownloadImage(
  dataUrl: string,
  fileName: string
): Promise<ExportResult> {
  const blob = dataUrlToBlob(dataUrl);
  if (canCopyImage()) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);
      return "copied";
    } catch {
      // 권한 거부 등 → 다운로드 폴백
    }
  }
  saveAs(blob, fileName);
  return "downloaded";
}

export function downloadImage(dataUrl: string, fileName: string): ExportResult {
  saveAs(dataUrlToBlob(dataUrl), fileName);
  return "downloaded";
}

function escapeCsvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** 표: 탭 구분(TSV) 복사 → 엑셀에 셀 단위로 붙여넣기 가능. 실패 시 CSV 다운로드. */
/** 첨부 전용 스프레드시트 수식 중화(첨부 draft §67) — 표시값은 그대로, 내보내기만 방어. */
const FORMULA_LEAD = new Set(["=", "+", "-", "@"]);
/** 파서가 수용하는 엄격 부호/그룹/지수 십진 문법 — 이 전체 일치만 +/- 예외로 숫자 유지. */
const STRICT_DECIMAL = /^[+-]?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?([eE][+-]?\d+)?$/;

export function neutralizeAttachmentCell(value: string): string {
  // 셀 내 탭·개행은 공백으로 — TSV/CSV 행·칸 구조 파괴 차단
  const flattened = value.replace(/[\t\r\n]/g, " ");
  const first = flattened.trimStart().charAt(0);
  if (!FORMULA_LEAD.has(first)) {
    return flattened;
  }
  if ((first === "+" || first === "-") && STRICT_DECIMAL.test(flattened.trim())) {
    return flattened; // 평범한 음수/양수 숫자는 엑셀에서 숫자로 남긴다
  }
  return "'" + flattened;
}

function exportCells(table: ChatTablePayload, neutralize: boolean): string[][] {
  const all = [table.columns, ...table.rows];
  return neutralize ? all.map((row) => row.map(neutralizeAttachmentCell)) : all;
}

export async function copyOrDownloadTable(
  table: ChatTablePayload,
  fileName: string,
  options?: { neutralize?: boolean }
): Promise<ExportResult> {
  const tsv = exportCells(table, options?.neutralize === true)
    .map((row) => row.join("\t"))
    .join("\n");
  if (canCopyText()) {
    try {
      await navigator.clipboard.writeText(tsv);
      return "copied";
    } catch {
      // 폴백
    }
  }
  downloadTableCsv(table, fileName, options);
  return "downloaded";
}

/**
 * 답변 글 복사 (2026-10-15). 클립보드 API 가 없거나(HTTP 사내망) 거부되면 숨긴 textarea + execCommand 로 한 번 더 시도한다.
 * 성공 여부만 돌려준다 — 실패해도 내려받기 폴백은 없다(글은 드래그로 복사할 수 있다)
 */
export async function copyChatText(text: string): Promise<boolean> {
  if (!text) return false;
  if (canCopyText()) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 아래 폴백
    }
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/** 표: UTF-8 BOM 부착 CSV 다운로드(엑셀 한글 깨짐 방지). */
export function downloadTableCsv(
  table: ChatTablePayload,
  fileName: string,
  options?: { neutralize?: boolean }
): ExportResult {
  const csv = exportCells(table, options?.neutralize === true)
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\r\n");
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  saveAs(blob, fileName);
  return "downloaded";
}

/**
 * 챗봇 생성 파일(PPT 보고서) 다운로드(v0.8.x) — <a href>로는 X-TENANT-ID 헤더를 못 실어
 * TenantFilter(FORBIDDEN)에 막히므로, 다른 API와 동일하게 헤더·쿠키를 실은 fetch로 받아
 * blob 저장한다. url은 BE 컨텍스트 경로(/snotes) 기준 상대 경로.
 */
export async function downloadChatFile(
  url: string,
  fileName: string
): Promise<"downloaded" | "failed"> {
  try {
    const response = await fetch(`/snotes${url}`, {
      credentials: "include",
      headers: {
        "X-TENANT-ID": global.getState().userInfo?.tenant_id || "DEFAULT",
      },
    });
    if (!response.ok) {
      return "failed";
    }
    saveAs(await response.blob(), fileName);
    return "downloaded";
  } catch {
    return "failed";
  }
}

/* ------------------------------------------------------------------ 대화 내보내기 (카톡처럼, 2026-10-15) */

/** 마크다운 기호를 걷어 카톡 내보내기처럼 글만 남긴다. 줄바꿈 · 목록은 그대로 */
function plainAnswer(text: string): string {
  return text
    .replace(/\*\*|__|`/g, "")
    .replace(/^#{1,6}\s+/gm, "")
    .trim();
}

/** 표를 글로 — 제목 한 줄 · 칸 이름 한 줄 · 행마다 한 줄, " | " 로 */
function tableText(table: ChatTablePayload): string {
  const lines: string[] = [];
  if (table.title) lines.push(table.title);
  lines.push(table.columns.join(" | "));
  for (const row of table.rows) lines.push(row.join(" | "));
  return lines.join("\n");
}

/**
 * 대화방 전체 → 카톡 PC 내보내기 모양의 글.
 *   제목 · 저장한 날짜 → 날짜가 바뀔 때마다 구분선 → "[이름] [오후 1:25] 내용"
 * 질문 · 답은 턴 시각 하나를 같이 쓴다(서버에 질문 시각이 따로 없다). 차트는 "(차트)" 로만
 */
export function buildChatExportText(
  title: string,
  userName: string,
  turns: ChatRoomTurn[],
  savedAt: Date
): string {
  const p = (n: number) => String(n).padStart(2, "0");
  const saved = `${savedAt.getFullYear()}-${p(savedAt.getMonth() + 1)}-${p(savedAt.getDate())} ${p(savedAt.getHours())}:${p(savedAt.getMinutes())}:${p(savedAt.getSeconds())}`;
  const out: string[] = [`SNotes AI Bot 대화 — ${title}`, `저장한 날짜 : ${saved}`, ""];
  let lastDay = "";
  for (const t of turns) {
    const day = formatChatDate(t.tmTurn);
    if (day && day !== lastDay) {
      out.push(`--------------- ${day} ---------------`);
      lastDay = day;
    }
    const time = formatChatTime(t.tmTurn);
    const stamp = time ? ` [${time}]` : "";
    if (t.question && t.question.trim()) {
      const quote = t.quote ? `(답장: ${t.quote.replace(/\s+/g, " ").trim().slice(0, 30)}…) ` : "";
      out.push(`[${userName}]${stamp} ${quote}${t.question.trim()}`);
    }
    const parts: string[] = [];
    parts.push(t.error ? `(실패한 요청) ${plainAnswer(t.answer || "")}` : plainAnswer(t.answer || "(답변이 비어 있습니다)"));
    if (t.tpBasis === "SCREEN" && t.tmScreen) parts.push(`(화면 기준 ${formatTurnTime(t.tmScreen)} 조회)`);
    if (t.table && Array.isArray(t.table.rows)) parts.push(tableText(t.table));
    if (t.chart) parts.push("(차트)");
    out.push(`[AI Center]${stamp} ${parts.join("\n")}`);
  }
  return out.join("\n") + "\n";
}

/** 글 파일로 받는다 — 메모장에서 바로 열리게 BOM · 윈도우 줄바꿈 */
export function saveChatText(text: string, fileName: string): void {
  saveAs(new Blob(["\uFEFF" + text.replace(/\n/g, "\r\n")], { type: "text/plain;charset=utf-8" }), fileName);
}
